import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';

import Isvg from 'react-inlinesvg';

import { FlyoutContent, FlyoutFooter } from 'components/Flyout';
import { FlyoutContentContext } from 'components/Flyout/FlyoutContext';
import { Button, Form } from 'components/HookForm';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { selectUserInfo } from 'selectors/general';

import ItemSaveErrorBanner from './ItemSaveErrorBanner';
import SkuItemFormFields from './SkuItemFormFields';
import SkuItemModifiersSlot from './SkuItemModifiersSlot';
import { messages } from './SkuAddItemForm.messages';

const { element } = bem('item-form');
const FORM_NAME = 'sku-add-item-form';

const SkuAddItemFormContent = ({
  isLoading,
  categoriesLoading,
  groups,
  subgroups,
  currencyCode,
  preselectedCategories,
  menuPriceDescription,
  formKey,
  onSubmit,
  onAddCategory,
  modifierGroups,
  modifierGroupsLoading,
  modifierGroupsLoadFailed,
  onRetryModifierGroups,
  onAddModifierGroup,
  showModifiersSection,
  saveFailure,
  onDismissSaveFailure,
}) => {
  const flyoutContentRef = useContext(FlyoutContentContext);
  const { adminUserId } = useSelector(selectUserInfo);
  const isAdminUser = !!adminUserId;

  useEffect(() => {
    if (saveFailure) flyoutContentRef?.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
  }, [saveFailure, flyoutContentRef]);

  const getDefaultValues = () => ({
    itemName: '',
    itemDescription: '',
    categories: preselectedCategories,
    menuPrice: '',
    linkToMenu: '',
    modifierGroupIds: [],
  });

  return (
    <Form
      key={formKey}
      isLoading={isLoading}
      id={FORM_NAME}
      defaultValues={getDefaultValues()}
      useFormOverrides={{ mode: 'onSubmit' }}>
      {({ formState, handleSubmit, watch, setValue }) => {
        const { errors, isSubmitting } = formState;
        const [watchCategories, itemName, itemDescription, menuPrice, linkToMenu, watchModifierGroupIds] = watch([
          'categories',
          'itemName',
          'itemDescription',
          'menuPrice',
          'linkToMenu',
          'modifierGroupIds',
        ]);

        const onInvalid = () => {
          flyoutContentRef?.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
        };

        const showFormErrorBanner = !_isEmpty(errors);

        return (
          <>
            <FlyoutContent>
              <When predicate={categoriesLoading}>
                <div className={element('loading-categories')}>
                  <LoadingDots />
                  <Message message={messages.loadingCategories} />
                </div>
              </When>
              <When predicate={Boolean(saveFailure)}>
                <ItemSaveErrorBanner failure={saveFailure} onDismiss={onDismissSaveFailure} />
              </When>
              <When predicate={showFormErrorBanner}>
                <div className={element('form-error-banner')}>
                  <Isvg src={ICONS.error} className={element('form-error-icon')} />
                  <Message message={messages.formSubmitError} />
                </div>
              </When>
              <SkuItemFormFields
                messages={messages}
                groups={groups}
                subgroups={subgroups}
                currencyCode={currencyCode}
                categories={watchCategories}
                onCategoriesChange={val => setValue('categories', val)}
                categoriesError={errors.categories}
                onAddCategory={onAddCategory}
                categoriesSaveFailed={Boolean(saveFailure?.categoriesFailed)}
                watchItemName={itemName}
                watchItemDescription={itemDescription}
                menuPriceDescription={menuPriceDescription}
                itemNameProps={{ placeholder: messages.itemNamePlaceholder }}
                pricingOptional={isAdminUser}
                priceVerificationSlot={
                  isAdminUser ? null : (
                    <div className={element('info-notice')}>
                      <div className={element('info-content')}>
                        <Isvg src={ICONS.infoBrand} className="info-icon" />
                        <Message message={messages.priceVerificationInfo} />
                      </div>
                    </div>
                  )
                }
                modifiersSlot={
                  showModifiersSection ? (
                    <SkuItemModifiersSlot
                      groups={modifierGroups}
                      value={watchModifierGroupIds}
                      onChange={val => setValue('modifierGroupIds', val)}
                      onAddModifierGroup={onAddModifierGroup}
                      isLoading={modifierGroupsLoading}
                      loadFailed={modifierGroupsLoadFailed}
                      onRetry={onRetryModifierGroups}
                      failed={Boolean(saveFailure?.modifiersFailed)}
                    />
                  ) : null
                }
              />
            </FlyoutContent>

            <FlyoutFooter>
              <Button
                styled="primary"
                className={element('submit-button')}
                onClick={handleSubmit(onSubmit, onInvalid)}
                waiting={isSubmitting}
                disabled={
                  categoriesLoading ||
                  !itemName?.trim() ||
                  !itemDescription ||
                  !watchCategories?.length ||
                  (!isAdminUser && (!menuPrice || !linkToMenu))
                }>
                <Message message={messages.submitButton} />
              </Button>
            </FlyoutFooter>
          </>
        );
      }}
    </Form>
  );
};

SkuAddItemFormContent.propTypes = {
  isLoading: PropTypes.bool,
  categoriesLoading: PropTypes.bool,
  groups: PropTypes.arrayOf(PropTypes.shape({})),
  subgroups: PropTypes.arrayOf(PropTypes.shape({})),
  currencyCode: PropTypes.string,
  preselectedCategories: PropTypes.arrayOf(PropTypes.shape({})),
  menuPriceDescription: intlMessage.isRequired,
  formKey: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onAddCategory: PropTypes.func,
  modifierGroups: PropTypes.arrayOf(PropTypes.shape({})),
  modifierGroupsLoading: PropTypes.bool,
  modifierGroupsLoadFailed: PropTypes.bool,
  onRetryModifierGroups: PropTypes.func,
  onAddModifierGroup: PropTypes.func,
  showModifiersSection: PropTypes.bool,
  saveFailure: PropTypes.shape({
    status: PropTypes.string,
    categoriesFailed: PropTypes.bool,
    modifiersFailed: PropTypes.bool,
  }),
  onDismissSaveFailure: PropTypes.func,
};

SkuAddItemFormContent.defaultProps = {
  isLoading: false,
  categoriesLoading: false,
  groups: [],
  subgroups: [],
  currencyCode: undefined,
  preselectedCategories: [],
  onAddCategory: null,
  modifierGroups: [],
  modifierGroupsLoading: false,
  modifierGroupsLoadFailed: false,
  onRetryModifierGroups: null,
  onAddModifierGroup: null,
  showModifiersSection: false,
  saveFailure: null,
  onDismissSaveFailure: null,
};

export default SkuAddItemFormContent;
